import {useEffect, useId, useState} from 'react';
import {clsx} from 'clsx';
import {useAvailableEmailsQuery} from '../../api/queries/use-available-emails-query';
import {usePostTestMailMutation} from '../../api/mutations/use-post-test-mail-mutation';
import styles from './test-mail-button.module.css';
import {TestMailButtonCheckBox} from './check-box';
import {useTestMailForm} from './form';
import {TestMailButtonRadioButton} from './radio-button';
import {MailTitleTextarea} from './mail-title-textarea';
import {TestMailButtonPullDown} from '@/components/unfixed-new-ui/test-mail-button-pull-down';
import {
  DialogRoot,
  DialogTrigger,
  DialogContent,
  DialogTitle,
  DialogClose,
} from '@/components/unfixed-new-ui/dialog';
import {useNotification} from '@/components/unfixed-new-ui/notification';

export type TestMailButtonProps = {
  readonly title: string;
  readonly isMailTitleFeatureEnable?: boolean;
  readonly prefixMailTitle?: string;
  readonly mailTitle?: string;
  readonly isMailTitlePrefix?: boolean;
  readonly isMailTitleReplacing?: boolean;
};

const otherOptionId = 0;

export function TestMailButton({
  title,
  isMailTitleFeatureEnable = false,
  prefixMailTitle = '',
  mailTitle = '',
  isMailTitlePrefix = false,
  isMailTitleReplacing = false,
}: TestMailButtonProps) {
  const id = useId();
  const mutation = usePostTestMailMutation();
  const {notify} = useNotification();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    trigger,
    formState: {errors},
  } = useTestMailForm({
    mailTitleType: isMailTitleReplacing
      ? 'replace'
      : isMailTitlePrefix
        ? 'prefix'
        : 'normal',
    mailTitle,
    isMailHederEnabled: true,
    mailHeader: '報道関係者各位',
    emailFieldValue: '',
  });

  // モーダルの開閉
  const [isOpen, setIsOpen] = useState(false);
  const [shouldShowErrors, setShouldShowErrors] = useState(false);

  // 送信ボタンが disabled になる条件
  const isExistErrorMessage =
    errors.mailTitle !== undefined ||
    errors.mailHeader !== undefined ||
    errors.emailFieldValue !== undefined;

  // メールアドレス
  const {data: emails} = useAvailableEmailsQuery();
  const options = emails
    ? [
        ...emails.map((row) => ({
          key: row.name,
          value: row.companyUserId.toString(),
        })),
        {key: 'その他', value: otherOptionId.toString()},
      ]
    : [];

  const [selectedCompanyUserId, setSelectedCompanyUserId] = useState<number>();
  const onSelect = (newValue: string) => {
    const n = Number(newValue);
    // 数字として有効な値でなければ return
    if (Number.isNaN(n)) return;

    setSelectedCompanyUserId(n);

    if (n === otherOptionId) {
      // その他を選択した場合、ユーザーがメールアドレスを入力するので、値を空にする
      setValue('emailFieldValue', '');
    } else {
      // ユーザーを選択した場合、値をそのユーザーのメールアドレスにする
      const email = emails?.find((row) => row.companyUserId === n)?.email;
      setValue('emailFieldValue', email ?? '');
      // メールアドレスフィールドのエラーを clear
      void trigger('emailFieldValue');
    }
  };

  // クエリで取得された emails の先頭の値を初期値に設定するための effect
  useEffect(() => {
    if (selectedCompanyUserId !== undefined) return;
    const user = emails?.[0];
    if (user === undefined) return;
    setSelectedCompanyUserId(user.companyUserId);
    setValue('emailFieldValue', user.email);
  }, [selectedCompanyUserId, emails, setValue]);

  const onSubmit = handleSubmit(
    async (data) => {
      try {
        // API call でメールを送信
        await mutation.mutateAsync({
          mailTitle: data.mailTitleType === 'replace' ? data.mailTitle : null,
          mailTitlePrefixCompanyNameFlg: data.mailTitleType === 'prefix',
          bodyHeadText: data.isMailHederEnabled ? data.mailHeader : null,
          email: data.emailFieldValue,
        });
        setIsOpen(false);
        notify({content: 'テストメールが送信されました'});
      } catch (error) {
        console.error(error);
        notify({content: 'テストメールの送信に失敗しました'});
      }
    },
    () => setShouldShowErrors(true), // eslint-disable-line @typescript-eslint/no-confusing-void-expression
  );

  // 入力内容変化時の操作
  useEffect(() => {
    const {unsubscribe} = watch((value, {name, type}) => {
      // 本文先頭行編集時、カスタムが選択されていなければ選択
      if (
        name === 'mailHeader' &&
        type === 'change' &&
        value.isMailHederEnabled === false
      ) {
        setValue('isMailHederEnabled', true);
      }

      if (
        name === 'mailTitle' &&
        type === 'change' &&
        value.mailTitleType !== 'replace'
      ) {
        setValue('mailTitleType', 'replace');
      }

      // 本文先頭行カスタムのチェックが変更された時、バリデーションを実行
      // （path が別のフィールドのため、そのままでは実行されない）
      if (name === 'isMailHederEnabled' && type === 'change')
        void trigger('mailHeader');

      if (name === 'mailTitleType' && type === 'change')
        void trigger('mailTitle');
    });
    return unsubscribe;
  }, [errors, setValue, watch, trigger]);

  return (
    <DialogRoot open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger>
        <button type='button' className={styles.trigger}>
          テストメール配信
        </button>
      </DialogTrigger>
      <DialogContent
        closeOnClickOutside={false}
        closeOnEscape={false}
        style={{padding: 0}}
      >
        <div className={styles.dialogMain}>
          <DialogTitle
            style={{
              fontSize: 21,
              fontWeight: 600,
              textAlign: 'center',
              letterSpacing: -0.42,
            }}
          >
            テストメール配信
          </DialogTitle>
          <form className={styles.form} onSubmit={onSubmit}>
            {isMailTitleFeatureEnable && (
              <div className={styles.formSection}>
                <p className={styles.formSectionLabel} id={`${id}-mailTitle`}>
                  メールタイトル
                </p>
                <div className={styles.formFields}>
                  <div className={styles.gap8}>
                    <fieldset className={styles.mailTitleFieldset}>
                      <TestMailButtonRadioButton
                        key='normal'
                        inputProps={{
                          value: 'normal',
                          'aria-labelledby': `${id}-mailTitle`,
                          'aria-describedby': `${id}-mailTitle-description`,
                          ...register('mailTitleType'),
                        }}
                      >
                        {title}
                      </TestMailButtonRadioButton>
                      <TestMailButtonRadioButton
                        key='prefix'
                        inputProps={{
                          value: 'prefix',
                          'aria-labelledby': `${id}-mailTitle`,
                          'aria-describedby': `${id}-mailTitle-description`,
                          ...register('mailTitleType'),
                        }}
                      >
                        {prefixMailTitle}
                      </TestMailButtonRadioButton>
                      <TestMailButtonRadioButton
                        key='replace'
                        inputProps={{
                          value: 'replace',
                          'aria-labelledby': `${id}-mailTitle`,
                          'aria-describedby': `${id}-mailTitle-description`,
                          'aria-label': 'mailTitle-enable',
                          ...register('mailTitleType'),
                        }}
                      >
                        <MailTitleTextarea
                          className={clsx(
                            styles.input,
                            styles.mailTitleTextarea,
                          )}
                          placeholder='メールタイトルを入力して下さい'
                          aria-labelledby={`${id}-mailTitle`}
                          aria-describedby={`${id}-mailTitle-description`}
                          aria-invalid={
                            shouldShowErrors && errors.mailTitle !== undefined
                          }
                          register={register('mailTitle')}
                        />
                      </TestMailButtonRadioButton>
                    </fieldset>

                    {shouldShowErrors && errors.mailTitle && (
                      <span role='alert' className={styles.error}>
                        {errors.mailTitle.message}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className={styles.formSection}>
              <p className={styles.formSectionLabel} id={`${id}-mailHeader`}>
                本文先頭行
              </p>
              <div className={styles.formFields}>
                <div className={styles.gap8}>
                  <TestMailButtonCheckBox
                    inputProps={{
                      'aria-labelledby': `${id}-mailHeader`,
                      'aria-describedby': `${id}-mailHeader-description`,
                      ...register('isMailHederEnabled'),
                    }}
                  >
                    <span className={styles.mailHeaderLabel}>
                      <input
                        type='text'
                        className={styles.input}
                        aria-labelledby={`${id}-mailHeader`}
                        aria-describedby={`${id}-mailHeader-description`}
                        aria-invalid={
                          shouldShowErrors && errors.mailHeader !== undefined
                        }
                        {...register('mailHeader')}
                      />
                      <span className={styles.mailHeaderPostfix}>
                        とメールの先頭行に挿入します。
                      </span>
                    </span>
                  </TestMailButtonCheckBox>

                  {shouldShowErrors && errors.mailHeader && (
                    <span role='alert' className={styles.error}>
                      {errors.mailHeader.message}
                    </span>
                  )}
                </div>
              </div>
              <p
                className={styles.description}
                id={`${id}-mailHeader-description`}
              >
                メディア関係者へ配信するプレスリリースメールの本文は、サブタイトル、リード文、本文、フッターで構成されます。メール本文の先頭行に、「報道関係者各位」など挿入することができます。
              </p>
            </div>

            <div className={styles.formSection}>
              <p className={styles.formSectionLabel} id={`${id}-email`}>
                メールアドレス
              </p>
              <div className={styles.emailField}>
                <div>
                  <TestMailButtonPullDown
                    value={selectedCompanyUserId?.toString() ?? ''}
                    options={options}
                    ariaLabel='送信先ユーザー'
                    onValueChange={onSelect}
                  />
                </div>

                <div className={styles.gap8}>
                  <input
                    type='email'
                    className={styles.input}
                    aria-labelledby={`${id}-email`}
                    aria-invalid={
                      shouldShowErrors && errors.emailFieldValue !== undefined
                    }
                    // ユーザー選択が「その他」の場合のみ入力可能
                    disabled={selectedCompanyUserId !== otherOptionId}
                    {...register('emailFieldValue')}
                  />
                  {shouldShowErrors && errors.emailFieldValue && (
                    <span role='alert' className={styles.error}>
                      {errors.emailFieldValue.message}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className={styles.buttons}>
              <DialogClose>
                <button
                  type='button'
                  className={clsx(styles.button, styles.buttonOutline)}
                >
                  キャンセル
                </button>
              </DialogClose>

              <button
                type='submit'
                className={clsx(styles.button, styles.buttonFlat)}
                disabled={shouldShowErrors && isExistErrorMessage}
              >
                送信
              </button>
            </div>
          </form>
        </div>
      </DialogContent>
    </DialogRoot>
  );
}
